export const DOCS_SECTIONS = [
  {
    group: "Getting Started",
    topics: [
      { slug: "getting-started", title: "Getting Started" },
      { slug: "installation", title: "Installation" },
      { slug: "terminal-hooks", title: "Terminal Hooks" },
    ],
  },
  {
    group: "Free Features",
    topics: [
      { slug: "layouts", title: "Layouts" },
      { slug: "pane-zoom", title: "Pane Zoom" },
      { slug: "drag-resize", title: "Drag to Resize" },
      { slug: "compose-bar", title: "Compose Bar" },
      { slug: "command-palette", title: "Command Palette" },
      { slug: "copy-mode", title: "Copy Mode" },
      { slug: "themes", title: "Themes" },
      { slug: "session-restore", title: "Session Restore" },
      { slug: "keybindings", title: "Custom Keybindings" },
      { slug: "agent-status", title: "Live Agent Status" },
      { slug: "triage-fleet", title: "Triage & Fleet" },
      { slug: "context-meter", title: "Context Window Meter" },
      { slug: "sentinel", title: "Sentinel" },
    ],
  },
  {
    group: "Pro Features",
    topics: [
      { slug: "broadcast", title: "Broadcast Prompts" },
      { slug: "workspace-presets", title: "Workspace Presets" },
      { slug: "mentions", title: "Cross-pane @mentions" },
      { slug: "macros", title: "Macros" },
      { slug: "multi-grid-spaces", title: "Multi-grid Spaces" },
      { slug: "git-worktree-panes", title: "Git Worktree Panes" },
      { slug: "worktree-fanout", title: "Worktree Fan-out" },
      { slug: "work-queue", title: "Keep-Busy Work Queue" },
      { slug: "scrollback-search", title: "Scrollback + Search" },
      { slug: "attention-notifications", title: "Attention Notifications" },
      { slug: "mcp-server", title: "MCP Server" },
      { slug: "auto-compact", title: "Auto-Compact" },
      { slug: "webserver", title: "Webserver" },
    ],
  },
  {
    group: "Account",
    topics: [{ slug: "trial-licensing", title: "Trial & Licensing" }],
  },
];

export const DOCS_CONTENT = {
  "getting-started": {
    title: "Getting Started",
    eyebrow: "Welcome",
    intro:
      "Poly Grid is a multi-terminal workspace for developers who run more than one thing at a time. This guide walks you through the core ideas in under five minutes.",
    sections: [
      {
        h2: "Core concepts",
        body: "Poly Grid arranges real PTY terminals on a tile grid. Each tile is a pane. A pane has a label, a working directory, a focus state, and an optional macro set. The compose bar above the grid routes input to the focused pane — or, in Pro, to a broadcast group.",
      },
      {
        h2: "Your first session",
        body: "When you launch Poly Grid for the first time you'll see a single 1×1 pane. Open the layout switcher and pick 2×2. Click a pane title to rename it. Type a command in the compose bar and press Enter — it goes to the pane with the cyan outline.",
      },
      {
        h2: "What to read next",
        body: "If you're brand-new, read Layouts and Compose Bar. If you've used a terminal multiplexer before, jump straight to Broadcast Prompts and Workspace Presets.",
      },
    ],
  },
  installation: {
    title: "Installation",
    eyebrow: "Setup",
    intro:
      "Poly Grid ships as a signed .dmg for macOS 12 and later (separate Apple Silicon and Intel builds) and as an AppImage for Linux. The download button auto-detects your platform.",
    sections: [
      {
        h2: "Download",
        body: "Click the Download button anywhere on the site — it picks the right build for your machine. The file is roughly 100 MB (Apple Silicon .dmg), 108 MB (Intel .dmg), or 110 MB (Linux .AppImage). You can always grab a different build straight from the GitHub release page.",
      },
      {
        h2: "Install on macOS",
        body: "Open the .dmg and drag Poly Grid.app into Applications. The first launch will prompt for Terminal accessibility once — this is required for the PTY backend.",
      },
      {
        h2: "Install on Linux",
        body: "The AppImage is self-contained — no installer. Mark it executable and run it: `chmod +x Poly-Grid-1.1.0.AppImage` then `./Poly-Grid-1.1.0.AppImage`. To get a desktop entry and menu icon, open it once and let your AppImage integration tool (e.g. Gear Lever or AppImageLauncher) offer to integrate it, or move it somewhere stable like `~/Applications` first.",
        code: "$ chmod +x Poly-Grid-1.1.0.AppImage\n$ ./Poly-Grid-1.1.0.AppImage",
      },
      {
        h2: "Updating",
        body: "Updates are auto-checked on launch. You can disable this in Settings → General. On Linux, replace the AppImage with the newer one from the release page.",
      },
    ],
  },
  "terminal-hooks": {
    title: "Terminal Hooks",
    eyebrow: "Setup",
    intro:
      "Terminal Hooks teach your coding agent to report what it's doing — working, awaiting input, awaiting permission, stalled, exited — instead of Poly Grid guessing from terminal output. They power Live Agent Status, the triage strip, the context meter, Auto-Compact, and hook-driven notifications. Each agent is enabled separately, and events post to a listener bound to 127.0.0.1 on your own machine — nothing leaves your computer.",
    sections: [
      {
        h2: "Enable Claude Code",
        body: "Open the app menu (the 'Poly Grid' menu on macOS) and choose 'Set up Claude Code status hooks…'. This adds Poly Grid's hook entries to your ~/.claude/settings.json and drops a small forwarding script in the app's data folder — no approval step is needed. Then start a new Claude Code session in a pane; hooks attach to sessions started after install, so restart any session that's already running.",
      },
      {
        h2: "Enable Codex",
        body: "From the same menu choose 'Set up Codex status hooks…'. This merges Poly Grid's entries into ~/.codex/hooks.json. Codex gates new hooks, so there's one extra step: run /hooks inside a Codex session and approve the Poly Grid hook when prompted. Then restart any running Codex session so it picks the hooks up.",
        code: "# one-time, inside a Codex session\n/hooks        # then approve the 'Poly Grid' hook",
      },
      {
        h2: "Enable opencode",
        body: "From the same menu choose 'Set up opencode status hooks…'. This installs a small plugin into ~/.config/opencode/plugin/. opencode loads plugins at startup, so restart any running opencode session for the plugin to take effect. No approval step is needed.",
      },
      {
        h2: "Confirm it's on",
        body: "Once any agent is set up, the toolbar pill reads 'Terminal Hooks: ON', and switches to 'Terminal Hooks — receiving events' the moment events start flowing. The per-pane header dots and the triage strip then track each agent's state live. (Hook setup is also reachable from the Notifications settings via 'Set up agent hooks' / 'Remove agent hooks'.)",
      },
      {
        h2: "Remove hooks",
        body: "Each agent has a matching item in the same menu — 'Remove Claude Code status hooks…', 'Remove Codex status hooks…', and 'Remove opencode status hooks…'. Removal only touches Poly Grid's own entries and leaves the rest of your agent config alone.",
      },
      {
        h2: "Nothing happens after setup",
        body: "Confirm hooks are ON for the agent you're actually running — the three install separately, so setting up Claude Code does not cover Codex or opencode. Start a new agent session, since hooks attach to sessions started after install. On Codex, make sure you accepted the /hooks approval prompt. Still nothing? Run the matching 'Remove …' then 'Set up … status hooks…' again.",
      },
    ],
  },
  layouts: {
    title: "Layouts",
    eyebrow: "Free",
    intro:
      "Five layouts ship in-box: 1×1, 2×1, 2×2, 3×2, and 3×3. You can add or remove panes at any time without losing scrollback in the panes that stay.",
    sections: [
      {
        h2: "Switching layouts",
        body: "Use the layout dropdown in the toolbar, or pick a layout from the command palette. Adding a pane fills the next open slot; removing a pane runs the close handler and clears scrollback for that cell only. (⌘1–⌘9 focus panes by index — they don't switch layouts.)",
      },
      {
        h2: "Per-cell controls",
        body: "Every pane header has three controls: clear (wipe the buffer), refit (force a TTY resize signal — useful after a viewport change), and close.",
      },
      {
        h2: "Focus and routing",
        body: "Only one pane is focused at a time. The focused pane gets an accent outline. The compose bar routes there by default. Click any pane to refocus.",
      },
    ],
  },
  "pane-zoom": {
    title: "Pane Zoom",
    eyebrow: "Free",
    intro:
      "Maximize the focused pane to fill the whole grid, then toggle back. Zoom is per-space and never destructive — the other panes are hidden, not closed, and nothing loses scrollback.",
    sections: [
      {
        h2: "Zooming a pane",
        body: "Press ⌘⇧Z, click the zoom button in the pane header, or pick 'Zoom / restore focused pane' from the command palette. The focused pane expands to fill the grid. Toggle the same pane again to restore the full grid; zoom a different pane to move the spotlight there.",
      },
      {
        h2: "Per-space and transient",
        body: "Each space tracks its own zoom independently, and zoom state is never persisted — any relayout (switching layout, loading a preset, adding a pane) drops back to the full grid. Drag-to-resize gutters are hidden while a pane is zoomed.",
      },
    ],
  },
  "drag-resize": {
    title: "Drag to Resize",
    eyebrow: "Free",
    intro:
      "Drag the gutter between two panes to make one bigger and its neighbor smaller. Switching to a uniform layout snaps every track back to even.",
    sections: [
      {
        h2: "Resizing a band",
        body: "Hover the thin gutter between two panes until the resize cursor appears, then drag. The two adjacent tracks trade space, and every pane refits its terminal live as it changes size — no odd wrapping while you drag.",
      },
      {
        h2: "Snapping back",
        body: "Custom sizes are per-space and transient; they're never persisted. Any relayout resets the grid to even tracks — pick a layout preset, load a workspace preset, add or move a pane, or toggle zoom, and the gutters return to uniform.",
      },
    ],
  },
  "compose-bar": {
    title: "Compose Bar",
    eyebrow: "Free",
    intro:
      "The compose bar is a single text area above the grid. It's the fastest way to send anything longer than a one-liner to a pane.",
    sections: [
      {
        h2: "How it works",
        body: "Type anything. Press Enter to send to the focused pane. Press Shift+Enter for a newline inside the compose bar. The status indicator on the right shows which pane will receive the input.",
      },
      {
        h2: "Multiline pastes",
        body: "Paste multiline content directly. Poly Grid sends it as a single chunk — no per-line latency, no character-by-character flooding.",
      },
      {
        h2: "@mentions (Pro)",
        body: "In Pro, the compose bar parses @paneN references and inlines the last 200 lines from those panes before sending. See Cross-pane @mentions for the full syntax.",
      },
    ],
  },
  "command-palette": {
    title: "Command Palette",
    eyebrow: "Free",
    intro:
      "A fuzzy-searchable list of everything Poly Grid can do — switch spaces, focus a pane, run a macro, change layout — without hunting through menus.",
    sections: [
      {
        h2: "Opening and searching",
        body: "Type to fuzzy-match against the action name and its group; ↑/↓ move the selection, Enter fires the highlighted action, Esc closes. Pro actions show dimmed when your license is locked and route to the activation modal instead of running.",
      },
      {
        h2: "What's in it",
        body: "Focus a pane by index or label, switch to or create a space, move the focused pane to another space, open the hidden-panes drawer, toggle broadcast, change layout, zoom the focused pane, enter copy mode, recall a paste buffer, run any macro, load any preset, and open each settings modal. Macros, presets, spaces, and paste buffers each generate one entry, so the list always reflects your current workspace.",
      },
    ],
  },
  "copy-mode": {
    title: "Copy Mode",
    eyebrow: "Free",
    intro:
      "Scroll, select, and copy a pane's scrollback entirely from the keyboard — no mouse. Yanked text lands on the clipboard and in a named paste buffer you can recall later.",
    sections: [
      {
        h2: "Entering and moving",
        body: "Press ⌘[ to enter copy mode on the focused pane. Move with ↑/↓ or j/k, page with PgUp/PgDn (or Ctrl+B / Ctrl+F), half-page with Ctrl+U / Ctrl+D, and jump to the top or bottom with g / G. A banner across the pane shows the key map. Press q or Esc to leave.",
      },
      {
        h2: "Selecting and yanking",
        body: "Press v (or Space) to drop a selection anchor on the current line, move to extend the selection, then y (or Enter) to yank. The yank copies to the system clipboard and pushes the text onto the named paste-buffer list.",
      },
      {
        h2: "Paste buffers",
        body: "Every yank is kept as a paste buffer — most-recent first, up to 20 — each shown by a short preview. Recall any of them from the command palette, which lists one 'Paste buffer: …' entry per stored buffer.",
      },
    ],
  },
  themes: {
    title: "Themes",
    eyebrow: "Free",
    intro:
      "Poly Grid ships with two themes — Default and Nord — and swaps the entire chrome plus xterm palette atomically. Theme choice persists across launches with no flash-on-cold-boot.",
    sections: [
      {
        h2: "Switching themes",
        body: "Open the Appearance ▾ dropdown in the toolbar and pick a theme. The change applies immediately to every pane and to the surrounding window chrome.",
      },
      {
        h2: "Why no flash?",
        body: "The chosen theme is read synchronously from disk before the first paint, so the window never renders the default theme briefly before swapping. Cold-boot launches feel instant.",
      },
      {
        h2: "Custom themes",
        body: "Custom themes are on the roadmap. The current shipping set is intentionally small to keep the palette curated.",
      },
    ],
  },
  "session-restore": {
    title: "Session Restore",
    eyebrow: "Free",
    intro:
      "When you quit Poly Grid, every space comes back exactly as you left it on next launch — all of your grids, not just the one in front. Restore is always on.",
    sections: [
      {
        h2: "What gets restored",
        body: "Every space and its order, which space was active, and for each space its layout, per-pane working directories, and pane labels. Worktree panes restore their git references (repo, branch, worktree path) so the close-time cleanup still works. Scrollback is not persisted — terminals start fresh.",
      },
      {
        h2: "What does not get restored",
        body: "Running processes are not resumed (this would require detaching the PTY, which we don't do). Transient view state — pane focus, zoom, drag-resized track sizes, and the broadcast group — resets on launch. If you need to bring back a stack of running commands, use a Workspace Preset with auto-run instead.",
      },
    ],
  },
  keybindings: {
    title: "Custom Keybindings",
    eyebrow: "Free",
    intro:
      "Remap any shortcut to your own muscle memory from the Keyboard Shortcuts panel. A last-pane toggle jumps you straight back to the pane you were just in.",
    sections: [
      {
        h2: "Rebinding a shortcut",
        body: "Open Keyboard Shortcuts (press ?, or pick it from the command palette). Each action has a Rebind button — click it and press the new chord. 'Reset to defaults' clears every override at once. Overrides are saved locally and re-applied on every launch.",
      },
      {
        h2: "Defaults worth knowing",
        body: "⌘⇧Z zoom, ⌘[ copy mode, ⌘; focus last pane (toggle), ⌘1–⌘9 focus a pane by index, ⌥⌘H/J/K/L move focus by direction, and ⌘⇧B toggle the broadcast group. When you record a chord, ⌘ and Control are interchangeable, as are Option and Alt.",
      },
    ],
  },
  "agent-status": {
    title: "Live Agent Status",
    eyebrow: "Free",
    intro:
      "Per-pane header dots and the triage strip below the compose bar show what each agent is doing — working, awaiting input, awaiting permission, stalled, or exited. It's powered by Terminal Hooks, which teach your coding agent to report its state instead of Poly Grid guessing from terminal output. Events post to a listener bound to 127.0.0.1 on your own machine; nothing leaves your computer.",
    sections: [
      {
        h2: "Turning on Terminal Hooks",
        body: "Each agent installs separately from the app menu — 'Set up Claude Code status hooks…', 'Set up Codex status hooks…', and 'Set up opencode status hooks…', each with a matching 'Remove …'. See Terminal Hooks for the full step-by-step (including the one-time Codex /hooks approval). The toolbar reads 'Terminal Hooks: ON' once installed, and 'Terminal Hooks — receiving events' when events are flowing.",
      },
      {
        h2: "What the status states mean",
        body: "working — the agent is mid-task. awaiting input — it finished its turn and is waiting on you. awaiting permission — it paused on a tool or permission prompt. stalled — no activity for a while during a turn. exited — the process ended.",
      },
      {
        h2: "Panes show no status, or the dots stay grey",
        body: "First, confirm hooks are ON for the agent you're running — the three install separately, so setting up Claude Code does not cover Codex or opencode. Second, start a new agent session in the pane; hooks attach to sessions started after install, so one that's already running won't report until it restarts. On Codex, accept its /hooks approval prompt the first time. If there's still nothing, run the matching 'Remove …' and then 'Set up … status hooks…' again.",
      },
    ],
  },
  "triage-fleet": {
    title: "Triage & Fleet",
    eyebrow: "Free",
    intro:
      "When an agent finishes, stalls, errors, or pauses for permission, a command bubble appears in the triage strip below the compose bar — a pill that tells you exactly which terminal needs you, in which space. Click it to jump straight there. The fleet drawer rolls every pane across every space into one cross-space view.",
    sections: [
      {
        h2: "Triage strip — pills that find the pane for you",
        body: "The triage strip sits just below the compose bar and stays empty while everything is working. The moment a pane changes to awaiting input, awaiting permission, error, or stalled, a bubble appears for it — labelled with the pane name, the space it lives in, and how long it's been in that state. Click the bubble to focus that pane, switching to its space first if it's in another grid. Bubbles clear themselves as soon as the pane goes back to work. It's driven by the same Terminal Hooks as Live Agent Status, so it needs hooks installed for the agent you're running.",
      },
      {
        h2: "Alert bubbles from Sentinel rules",
        body: "When Sentinel's safety rules fire, the strip also shows alert bubbles, colour-coded by severity — critical (red), warn (yellow), info (blue) — naming the rule that matched. These appear whether or not Sentinel's model monitoring is on, because the safety rules are deterministic. See Sentinel for the rule set.",
      },
      {
        h2: "The fleet drawer",
        body: "Click the Sentinel pill at the top-right to open the fleet drawer — an 'Agents' panel that lists every pane grouped by space, each with its live state and Sentinel's latest one-line summary. A summary line at the top counts the whole fleet at a glance, for example '5 agents · 2 working · 1 awaiting permission · 1 error · 1 done'. Standup copies a markdown digest of the last 24 hours to the clipboard; the gear opens Sentinel settings.",
      },
      {
        h2: "Workflow examples",
        body: "(1) Five agents, one of you: kick off work in five panes across two spaces, then ignore them until a bubble appears — when one stalls or asks permission, the strip tells you which space to switch to and one click lands you on the exact pane. (2) Fleet at a glance: leave the fleet drawer open on a second monitor; the summary line is your dashboard for how many agents are working versus waiting on you, and Standup gives you a paste-ready end-of-day digest.",
      },
    ],
  },
  "context-meter": {
    title: "Context Window Meter",
    eyebrow: "Free",
    intro:
      "Each pane header can show live token usage for a Claude Code session — for example, `82k ctx`. It reads the Claude Code transcript at each turn boundary, so the count is exact rather than estimated.",
    sections: [
      {
        h2: "Requirements",
        body: "The meter reads the Claude Code transcript, so it needs Terminal Hooks installed for Claude Code (see Live Agent Status) and an active Claude Code session in the pane. It shows nothing for other agents.",
      },
      {
        h2: "The meter is blank",
        body: "Confirm Terminal Hooks are installed for Claude Code and that the pane is running an active Claude Code session. The meter updates at the end of each turn, not mid-stream, so a freshly started session shows a value once its first turn completes.",
      },
    ],
  },
  sentinel: {
    title: "Sentinel",
    eyebrow: "Free",
    intro:
      "Sentinel is an opt-in safety watchdog. It watches pane output, summarizes what each agent is doing, and raises safety alerts. It observes, never acts — it never sends keystrokes or touches your agents. Sentinel runs on a model you connect (there is no bundled model); the deterministic safety rules need no model at all.",
    sections: [
      {
        h2: "Turning it on",
        body: "The 'Sentinel watchdog' prompt asks how to connect a model: 'Anthropic (Claude)' uses your Claude subscription or an Anthropic API key; 'OpenAI (ChatGPT)' uses an OpenAI API key; 'OpenAI-compatible server (Ollama, MiniMax, …)' uses a local or self-hosted model; and 'Not now' declines cleanly — the Sentinel UI disappears and never re-prompts. You can change any of this later from the Sentinel… menu.",
      },
      {
        h2: "Monitoring and model backends",
        body: "Under Sentinel… → Monitoring, 'Enable monitoring (classify, summarize, triage)' is the master switch for all model-backed work. Then pick one model backend. Anthropic (Claude): authenticate with 'Use my Claude subscription (setup token)' — paste the `sk-ant-oat01-…` token from `claude setup-token` and Save — or 'Use an Anthropic API key'; the default model is `claude-haiku-4-5-20251001`. OpenAI (ChatGPT): paste an API key; the default model is `gpt-4o-mini`. OpenAI-compatible server (Ollama, MiniMax, …): set the Base URL (default `http://127.0.0.1:11434/v1` for Ollama), use Probe to list the models the server offers, pick a Model (for example `llama3.2` or `qwen2.5`), and leave the API key blank for a local server. Every backend has Per hour and Per day rate caps.",
        code: "# Anthropic: paste this token into the 'Setup token' field\n$ claude setup-token        # prints an sk-ant-oat01-… token\n\n# OpenAI-compatible (Ollama) default Base URL\nhttp://127.0.0.1:11434/v1",
      },
      {
        h2: "The classify toggle",
        body: "'Enable classify jobs via this model' controls frequent classification. It's off by default for the Anthropic and OpenAI backends, because classify runs often and keeping it off controls cost, and on by default for a local OpenAI-compatible server, because local inference is cheap.",
      },
      {
        h2: "Triggers and the fleet digest",
        body: "Under Triggers, Sentinel does model work when a pane changes to stalled (summarize what the agent is doing), when a pane changes to error (summarize what went wrong), on session stop (summarize what the agent did), and on a rule hit (enrich the alert with severity). 'Enable periodic fleet digest (cross-pane summary)' produces a recurring cross-pane summary on an interval you set, in the 5–1440 minute range.",
      },
      {
        h2: "Safety rules (no model required)",
        body: "The deterministic safety rules fire with no model at all, even when monitoring is off: block force-push to protected branches (main/master/release*), warn on recursive deletion outside the assigned worktree, warn on `git reset --hard` or `git clean -fd`, and warn on destructive DB or migration operations. Add your own under Custom patterns. A rule hit shows up as an alert bubble in the triage strip — see Triage & Fleet.",
      },
      {
        h2: "The Sentinel pill, the fleet drawer, and token budget",
        body: "Once a model backend is configured, a Sentinel pill appears at the top-right showing its state (idle, or pulsing while it's analysing a pane) and the day's model-token spend. Click it to open the fleet drawer — the 'Agents' panel that lists every pane grouped by space with each agent's live state and latest summary; 'Standup' there copies a 24-hour markdown digest to the clipboard, and the gear opens these settings (see Triage & Fleet). The drawer also shows a 'Sentinel model usage today' gauge against a daily token target you can click to set, so model spend stays visible and capped.",
      },
      {
        h2: "It's not classifying or summarizing",
        body: "'Enable monitoring' must be on, and the backend must have valid credentials — a saved token or key, or a reachable Base URL for the compatible server. For the Anthropic and OpenAI backends, classify is off by default, so turn on 'Enable classify jobs via this model' if you want frequent classification, and check that your Per hour / Per day caps aren't exhausted. For a local OpenAI-compatible server, use Probe to confirm the URL and model are reachable.",
      },
      {
        h2: "A fully local setup, and turning it off",
        body: "For a fully local, private setup, choose the OpenAI-compatible server backend and point it at a local Ollama (`http://127.0.0.1:11434/v1`) — nothing leaves your machine. There's no bundled model, so install the local model in Ollama yourself. To turn Sentinel off, toggle it from the Sentinel… menu; with monitoring off, the deterministic safety rules still run. Sentinel is opt-in and never re-prompts after you decline.",
      },
    ],
  },
  broadcast: {
    title: "Broadcast Prompts",
    eyebrow: "Pro",
    intro:
      "Pick a group of panes, type once, send to all. Broadcasting panes get a visible orange border so you always know which terminals are receiving.",
    sections: [
      {
        h2: "Starting a broadcast",
        body: "Hold ⌥ and click pane titles to add them to the broadcast group. Type into the compose bar — your input goes to every pane in the group simultaneously. The orange border pulses on broadcasting panes.",
      },
      {
        h2: "Common patterns",
        body: "Run the same git command across three repo checkouts. Tail logs in five environments and apply a filter to all of them. Run a smoke test against staging, prod, and a feature branch in lockstep.",
        code: "# Broadcast: pane1, pane2, pane3\n$ git pull --rebase\n$ yarn install\n$ yarn test --watch=false",
      },
      {
        h2: "Exit broadcast",
        body: "⌥-click any broadcasting pane title to remove it from the group. ⌘⇧B clears the broadcast group entirely.",
      },
      {
        h2: "Workflow examples",
        body: "Two patterns this earns its keep on. (1) Rolling a dependency bump across a microservice cluster: open a pane per repo, add them all to the broadcast group, type the upgrade + test sequence once, watch them go in lockstep. (2) Validating a config change across environments: point pane1 at dev, pane2 at staging, pane3 at prod, broadcast a single `curl -s $URL/healthz | jq .version` — the orange border tells you which terminals just got the request and you can compare the three responses side by side.",
        code: "# Broadcast group: api-svc, auth-svc, billing-svc\n$ pnpm up @company/shared@^2.4.0\n$ pnpm install\n$ pnpm test -- --run\n$ git diff --stat",
      },
    ],
  },
  "workspace-presets": {
    title: "Workspace Presets",
    eyebrow: "Pro",
    intro:
      "Named, reusable bundles of layout + per-pane cwd + per-pane initial prompt + broadcast group. Optional auto-run-on-load. Whole-library JSON export and import.",
    sections: [
      {
        h2: "Creating a preset",
        body: "Configure a workspace exactly how you want it — pick a layout, navigate each pane to a working directory, optionally start a long-running process — then open Presets → Save current… and give it a name.",
      },
      {
        h2: "Auto-run-on-load",
        body: "Each pane in a preset can store an 'initial prompt' that runs when the preset is loaded. Enable Auto-run on load if you want the prompts to send automatically; otherwise they're staged in the compose bar so you can review them first.",
      },
      {
        h2: "Export / import",
        body: "Export a single preset or the whole library as JSON. The format is human-readable and version-controllable.",
        code: '{\n  "preset": "api-debug",\n  "layout": "2x2",\n  "panes": [\n    { "label": "api",    "cwd": "~/projects/api",  "initial": "yarn dev" },\n    { "label": "logs",   "cwd": "~/projects/api",  "initial": "tail -f logs/server.log" },\n    { "label": "db",     "cwd": "~",               "initial": "psql polygrid" },\n    { "label": "shell",  "cwd": "~/projects/api" }\n  ],\n  "broadcast": [],\n  "autorun": true\n}',
      },
      {
        h2: "Workflow examples",
        body: "(1) Your dev stack as one click: a 2×2 preset with api, web, db, and shell panes and auto-run on, so `Presets ▸ api-debug` replaces five minutes of cd + start-services with a single dropdown pick. (2) Onboarding a new teammate: export your library to JSON, drop it in the repo at `.polygrid/presets.json`, and the first thing they do after install is import — they now see your exact layout, working directories, and startup commands.",
      },
    ],
  },
  mentions: {
    title: "Cross-pane @mentions",
    eyebrow: "Pro",
    intro:
      "Reference another pane's output from the compose bar. @pane2 inlines the last 200 lines of pane2. @pane2:50 overrides the line count.",
    sections: [
      {
        h2: "Syntax",
        body: "@<handle> inlines the last 200 lines from that pane into the message you're sending. @<handle>:N overrides the line count, where N is clamped to 1–1000.",
        code: "# Inline last 200 lines from pane2\n@pane2 summarize the errors above\n\n# Inline last 50 lines\n@pane2:50 what changed in the most recent commit?",
      },
      {
        h2: "Handles",
        body: "Handles are derived from pane labels — slugified (lowercased, non-alphanumerics replaced with -). If two panes slug to the same handle, a positional fallback (paneN) is used. The handle dropdown in the compose bar shows the current mapping.",
      },
      {
        h2: "Where it sends",
        body: "An @-mentioned message still routes to the focused pane (or broadcast group). The mention only inlines text — it does not change the destination.",
      },
      {
        h2: "Workflow examples",
        body: "(1) LLM-assisted debugging: keep a Claude or Codex pane focused, and pipe in failing test output with `@tests:200 why is this assertion flaking?` — the model sees the actual error, not your paraphrase of it. (2) Diffing two services: with `@api-a:30 vs @api-b:30 — what's different between these JSON responses?` you can hand both pane buffers to an LLM in one shot, without juggling tmp files or copy/paste.",
        code: "# Focused pane is a Claude session; tests pane is running `vitest`\n@tests:200 the BillingService test is failing — explain the stack trace\n\n# Compare two API responses\n@api-a:30 vs @api-b:30 — which fields differ in the user payload?",
      },
    ],
  },
  macros: {
    title: "Macros",
    eyebrow: "Pro",
    intro:
      "User-defined {label, command, scope} buttons in each pane header. Up to 5 visible + ⋯ overflow per pane. Per-macro focused or broadcast scope.",
    sections: [
      {
        h2: "Adding a macro",
        body: "Open Manage Macros from any pane header. Each macro has three fields — label (button text), command (what runs), scope (focused or broadcast). Full CRUD plus drag-reorder.",
      },
      {
        h2: "Scopes",
        body: "Focused scope runs the command in the pane you click from. Broadcast scope runs it in every pane currently in the broadcast group, even if you click the macro from outside the group.",
      },
      {
        h2: "Examples",
        body: "Common macros: clear-and-rerun, git fetch + status, restart-dev-server, attach-debugger. The five most-recently-used surface in the header; the rest live in the ⋯ overflow.",
      },
      {
        h2: "Workflow examples",
        body: "(1) The dev-loop macro: bind `clear && pnpm test -- --run && say done` to a single button on your tests pane — one click ends every iteration, no muscle memory required. (2) Broadcast restart: a macro with scope=broadcast that runs `kill %1 2>/dev/null; pnpm dev &` will restart every dev server in the broadcast group in one click — useful when you change a shared package and need every consumer to pick up the new version at once.",
      },
    ],
  },
  "multi-grid-spaces": {
    title: "Multi-grid Spaces",
    eyebrow: "Pro",
    intro:
      "Up to 16 separate grids, each with its own panes, layout, broadcast group, and focus. Switch with the Spaces ▾ dropdown or the command palette. A preset can load directly into a fresh space.",
    sections: [
      {
        h2: "Creating and switching spaces",
        body: "Spaces ▾ → New space spins up an empty 1×1 in a fresh grid. The dropdown shows every space with its pane count and the currently focused pane. The command palette searches spaces by name.",
      },
      {
        h2: "What each space owns",
        body: "Layout, panes, working directories, labels, broadcast group, focused pane, and macro overrides are all per-space. Themes, MCP server state, and license are global. Closing a pane in space A has no effect on space B — and the orphan drawer keeps shrunk-away panes grouped by which space they came from.",
      },
      {
        h2: "Loading a preset into a space",
        body: "From the Presets dropdown, pick Load into new space — your current grid is untouched, the preset opens cleanly in a fresh tab. Combine with auto-run-on-load and you have a single click that boots a complete second workspace.",
      },
      {
        h2: "Workflow examples",
        body: "(1) Client A / client B / personal: a space per context, switched in one keystroke. Client A's repos, services, and logs stay live in their own grid while you spend an hour on client B; no closing tabs, no losing focus. (2) Incident war-room: when a page fires, open a temporary space with grafana, the failing service's logs, and a fix-branch pane. Resolve the incident, close just that space — your day-job state in the other spaces is exactly how you left it.",
      },
    ],
  },
  "git-worktree-panes": {
    title: "Git Worktree Panes",
    eyebrow: "Pro",
    intro:
      "Spin up a branch as its own pane in one click. Pick a repo, name a branch, and the app runs `git worktree add` and opens a fresh terminal in the new tree. Closing the pane prompts to clean up the worktree.",
    sections: [
      {
        h2: "Creating a worktree pane",
        body: "Right-click any grid cell → New worktree pane… A modal asks for the repo (defaulting to the focused pane's cwd if it's a git repo), the branch name (existing or new), and whether to track a remote ref. The app runs `git worktree add ../<repo>-<branch> <branch>` and opens a PTY in that directory.",
        code: "# What runs under the hood\n$ git -C ~/projects/api worktree add ../api-pr-2317 origin/feature/billing-v2\n$ cd ../api-pr-2317",
      },
      {
        h2: "Cleanup on close",
        body: "When you close a worktree pane, Poly Grid prompts to delete the worktree. If the working tree is dirty, it offers a one-click --force retry so you don't have to drop to a shell. The standalone `git worktree remove` flow stays free (the cleanup prompt works for everyone), so past customers can keep tidying up old trees.",
      },
      {
        h2: "Workflow examples",
        body: "(1) PR review without disturbing in-flight work: a teammate's branch lands for review — new worktree pane on `origin/their-branch`, run the failing test, poke at the diff, close the pane to delete the tree. Your feature branch is exactly where you left it, no stash dance. (2) Hot-fix on top of main: production breaks. New worktree pane on `main`, branch named `hotfix-123`, fix, push, close. Meanwhile your half-rebased feature branch in the original pane is untouched.",
      },
    ],
  },
  "worktree-fanout": {
    title: "Worktree Fan-out",
    eyebrow: "Pro",
    intro:
      "One task, N isolated git-worktree panes — each on its own branch, each auto-running the same prompt — so you can race several agents at the same problem and compare approaches side by side.",
    sections: [
      {
        h2: "Launching a fan-out",
        body: "Open 'Fan out a task to N worktrees…' from the command palette. Pick the repo and a base branch, set how many worktrees you want, and write the task prompt. Poly Grid creates that many worktree panes in the current space, each on a fresh branch off the base, and feeds the prompt into each pane once it's ready to receive — so every agent starts from the same instruction in its own isolated tree.",
      },
      {
        h2: "Partial-failure tolerant",
        body: "If some worktrees can't be created (a branch name collides, the tree is dirty), the ones that succeed still spawn and run. The modal stays open and reports what was created versus what failed and why, so a single bad branch never sinks the whole batch.",
      },
      {
        h2: "Cleanup",
        body: "Each fan-out pane is a normal worktree pane — closing it offers the same worktree cleanup prompt, with a one-click --force retry if the tree is dirty. Tidy up the branches you don't want to keep and merge or cherry-pick from the one you do.",
      },
    ],
  },
  "work-queue": {
    title: "Keep-Busy Work Queue",
    eyebrow: "Pro",
    intro:
      "Queue up prompts for a pane and Poly Grid feeds them in one at a time as the agent finishes each turn — so an idle agent never sits waiting on you, and you never double-feed it.",
    sections: [
      {
        h2: "Queueing prompts",
        body: "Each pane has a queue button in its header (also reachable from the command palette). Open it, type a prompt, and add it to that pane's list. The queue is per-pane — every terminal keeps its own backlog — and you can pause a pane's queue without clearing it.",
      },
      {
        h2: "One per turn, no double-feed",
        body: "When a pane goes idle — it transitions to 'awaiting input' after working, stalling, or pausing for permission — the next queued prompt is sent automatically, exactly one per turn. On send, the pane is marked working immediately, so a fast second item can't slip in before the agent actually starts. Nothing is fed while a pane has no live agent session.",
      },
      {
        h2: "Workflow examples",
        body: "(1) Batch a refactor: queue 'fix the type errors', 'add tests', 'update the changelog' onto a Claude pane and walk away — each step kicks off the moment the previous one lands. (2) Overnight backlog: line up a stack of independent chores per pane before you leave; the queue keeps each agent fed turn after turn instead of stopping at the first done.",
      },
    ],
  },
  "scrollback-search": {
    title: "Scrollback + Cross-pane Search",
    eyebrow: "Pro",
    intro:
      "Opt-in per-pane on-disk scrollback capture, plus a Cmd+Shift+F overlay that searches every pane's live buffer and every capture together — with click-to-jump and a `live` tag when no capture is present.",
    sections: [
      {
        h2: "Enabling capture per pane",
        body: "Pane header → Scrollback ▾ → Enable capture. Capture is ANSI-stripped, tail-rotated (oldest lines fall off when the cap fills), and size-capped per pane in the [1 MB, 200 MB] range — default 10 MB. The cap is enforced as you write, so the file never gets bigger than you set.",
      },
      {
        h2: "Searching across panes",
        body: "Cmd+Shift+F opens the global search overlay. It searches every pane's live xterm buffer and every on-disk capture in parallel, then ranks results by recency. Each hit shows pane label, timestamp, and three lines of surrounding context. Enter jumps to the source pane and scrolls to the match; if the match is from a capture rather than the live buffer, the result is tagged accordingly.",
      },
      {
        h2: "Live vs captured",
        body: "Panes without capture enabled are still searched — but only as far back as their xterm scrollback (5,000 lines, RAM only). Panes with capture enabled are searchable as far back as their cap allows. A small `live` tag in the search result tells you which one you're looking at.",
      },
      {
        h2: "Workflow examples",
        body: "(1) Postmortem: after a 4-hour test run that finally failed, Cmd+Shift+F for the assertion message tells you the first time it flaked — across every pane, including the now-rotated build logs you'd otherwise have lost. (2) \"Where did I see that?\": you remember running into a connection string three days ago in some pane you've since closed. Capture was on, the closed pane's tail is still on disk under your old space; one search finds it and click-jumps you to the orphan drawer entry.",
        code: "# Capture file layout on disk\n~/Library/Application Support/Poly Grid/scrollback/\n  <space-id>/\n    <pane-id>.log     # ANSI-stripped, tail-rotated, capped per setting",
      },
    ],
  },
  "attention-notifications": {
    title: "Attention Notifications",
    eyebrow: "Pro",
    intro:
      "OS desktop notifications, dock bounce, and an optional sound whenever a background or unfocused pane goes idle, rings the terminal bell, or exits. Per-pane Mute. Notification body is space-aware so you know which grid is asking for you.",
    sections: [
      {
        h2: "When a notification fires",
        body: "Three triggers, all configurable in Settings → Notifications. (1) Idle: pane produces no output for N seconds after producing some (good for long builds). (2) Bell: the terminal emits a \\a (great for scripts that opt in). (3) Exit: the shell or long-running process exits. The pane must be unfocused or its grid must be backgrounded — focused-pane activity never fires.",
      },
      {
        h2: "Hook-driven state (Terminal Hooks)",
        body: "'Use event bus for accurate agent state (requires hooks)' switches notifications from guessing at agent state from terminal output to real, hook-driven state. Turning it on unlocks per-state toggles — 'Waiting for permission', 'Error', 'Stalled', and 'Waiting for input (idle)' — plus a stall threshold. 'Set up agent hooks' / 'Remove agent hooks' install the same Terminal Hooks used by live agent status; see Live Agent Status for the per-agent setup. 'Play a sound with notifications' is configured here too.",
      },
      {
        h2: "Per-pane mute",
        body: "Some panes (a tail of an always-noisy log) you never want to hear from. Pane header → Mute notifications silences just that pane without touching the global setting. Mute persists across launches and is reflected with a small bell-with-slash glyph on the pane header.",
      },
      {
        h2: "Notification body format",
        body: "`<pane label> – <space name> – <state>`. So `tests – api-debug – idle` tells you not just which pane to look at but which space to switch to first. macOS Notification Center groups by app, so a flurry of completions reads as one stack.",
      },
      {
        h2: "Workflow examples",
        body: "(1) Hour-long test suites stop being a babysitting job. Kick off `pnpm test:e2e`, switch to a different space and work on something else, get a desktop notification the moment it finishes or breaks. (2) Bell-on-error workflow: wrap your dev loop in `cmd || printf '\\a'` so failures ring the terminal bell. Set the bell trigger on, idle trigger off, and now your dock only bounces for the cases that need you — successful runs are silent.",
        code: "# Bell-on-error pattern\n$ pnpm test --watch=false || printf '\\a'\n\n# Silence one always-noisy pane without touching globals\n# Pane header ⋯ → Mute notifications",
      },
    ],
  },
  "mcp-server": {
    title: "MCP Server",
    eyebrow: "Pro",
    intro:
      "A local HTTP server bound to 127.0.0.1 that exposes three tools — list_panes, read_pane, write_pane — to LLM clients (Claude Desktop, Cursor, Cline) over JSON-RPC at /mcp and plain REST at /api/v1.",
    sections: [
      {
        h2: "Why",
        body: "Your LLM can see your terminals. Ask Claude to read the last failed test run, summarize the diff, and write the fix into a pane — all without copy/paste. Or wire Cursor to your `yarn dev` log so it can correlate compile errors with the file you're editing.",
      },
      {
        h2: "Security model",
        body: "The server binds to 127.0.0.1 only. Every request requires a 32-byte bearer token (regeneratable). An origin allowlist gates browser-style requests. Each pane has explicit readable/writable opt-in — writable panes get a red inset border so you can always see what's exposed.",
      },
      {
        h2: "Ports",
        body: "Defaults to 8473 and falls back through 9 ports on conflict (8473–8482). The active port is shown in the status bar and copied with one click.",
      },
      {
        h2: "Endpoints",
        body: "JSON-RPC at /mcp for MCP-compatible clients, and a plain REST surface at /api/v1 for everything else.",
        code: "GET  /api/v1/panes\nGET  /api/v1/panes/{id}/output?lines=200\nPOST /api/v1/panes/{id}/input    { \"text\": \"ls -la\\n\" }",
      },
      {
        h2: "Claude Desktop config",
        body: "Add this to ~/.config/claude/claude_desktop_config.json (or the macOS equivalent at ~/Library/Application Support/Claude/claude_desktop_config.json):",
        code: '{\n  "mcpServers": {\n    "polygrid": {\n      "transport": "http",\n      "url": "http://127.0.0.1:8473/mcp",\n      "headers": { "Authorization": "Bearer <32-byte-token>" }\n    }\n  }\n}',
      },
      {
        h2: "Workflow examples",
        body: "(1) Claude fixes the failing test: with the `tests` pane marked readable and the `editor` pane marked writable, you say \"the BillingService test is broken — look at the output and write a fix\" in Claude Desktop. Claude calls read_pane on `tests`, drafts a patch, and write_panes it into your editor pane — no copy/paste, and the red border on `editor` is your visible reminder that something just touched your shell. (2) Cursor correlates compile errors to edits: mark your `yarn dev` pane readable, and Cursor can pull the most recent TypeScript error and jump you to the offending file — without you tabbing back to the terminal.",
      },
    ],
  },
  "auto-compact": {
    title: "Auto-Compact",
    eyebrow: "Pro",
    intro:
      "Auto-Compact sends `/compact` to a pane once its context crosses your threshold, at a clean turn boundary — so panes stay lean and per-turn spend stays down. It builds on the free context window meter.",
    sections: [
      {
        h2: "Settings (menu Auto-compact…)",
        body: "'Auto-run /compact when a pane crosses the threshold' is the on/off switch, off by default. 'Threshold' sets the token count that triggers a compact — default `140000`, minimum `1000`, in steps of `1000`. When your license is locked, 'Buy — $20 / Activate' appears instead of the controls, because Auto-Compact is a Pro feature.",
      },
      {
        h2: "It never triggers",
        body: "Auto-Compact builds on the context meter, so it needs the same prerequisites — Terminal Hooks for Claude Code and an active Claude Code session. Make sure the switch is on and the threshold is set. It only fires at a turn boundary, so it won't interrupt mid-response.",
      },
    ],
  },
  webserver: {
    title: "Webserver",
    eyebrow: "Pro",
    intro:
      "The webserver serves a mobile terminal UI from your machine, so you can view and drive your panes from a phone or tablet. It's off by default.",
    sections: [
      {
        h2: "Settings (menu Webserver…)",
        body: "'Enable webserver' is the on/off switch. 'Port' sets the listening port — default `8484`, range 1024–65535 — and if the port is busy it falls back to the next free one, so always copy the URL the modal shows. 'Require password to connect' gates access behind a login; use the 'New password' field and 'Set password' to store a login password (hashed, never kept in plaintext), and 'Clear' to remove it. The modal lists the URLs to open on your phone. When your license is locked, 'Buy — $20 / Activate' appears instead of the controls, because the webserver is Pro.",
      },
      {
        h2: "My phone can't reach it",
        body: "Confirm it's enabled — the toolbar pill reads 'Webserver' — and use a URL from the modal, not `localhost`. The phone and computer must be on the same Wi-Fi, and the network must allow device-to-device connections; some guest or public networks block this. If you changed the port, re-copy the URL, since it may have fallen back to another port.",
      },
      {
        h2: "Using it from outside your home network",
        body: "Yes — the webserver binds to your LAN, so anything that can reach your machine can serve it. Point Tailscale, a Cloudflare tunnel, or any reverse proxy at the port and use it from anywhere. It's a terminal, so treat it like one: turn on 'Require password to connect' and set a password before you expose it beyond your own Wi-Fi, and let the tunnel handle TLS.",
      },
    ],
  },
  "trial-licensing": {
    title: "Trial & Licensing",
    eyebrow: "Account",
    intro:
      "Poly Grid is free forever for the base tier. Pro features unlock for 7 days from first launch, then require activation.",
    sections: [
      {
        h2: "Trial mechanics",
        body: "The trial timer starts on first launch and runs for 7 days of real-world time. Every Pro feature is fully enabled during the trial — there is no nag, no watermark, no degraded mode. When the timer expires, attempting to use a locked feature opens the activation modal instead of running.",
      },
      {
        h2: "Pricing",
        body: "$20 one-time. The license activates 3 machines and is good for life. No subscription. No renewals. No seat math.",
      },
      {
        h2: "Managing machines",
        body: "Settings → License shows your three slots. You can deactivate a machine remotely to free up a slot — useful when you sell or wipe a laptop.",
      },
      {
        h2: "Refunds",
        body: "30-day no-questions refunds if you decide Poly Grid isn't for you. Email support@poly-grid.com.",
      },
    ],
  },
};
